<script lang="ts">
  import Icon from './Icon.svelte';
  import { photoUrl } from './api';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import type { PoiView, GroupView } from '~/shared/api.js';

  let {
    poi,
    groups = [],
    onOpen,
  }: {
    poi: PoiView;
    groups?: GroupView[];
    onOpen: (poi: PoiView) => void;
  } = $props();

  const poiGroups = $derived(
    poi.groupIds
      .map((id) => groups.find((g) => g.id === id))
      .filter((g): g is GroupView => g !== undefined),
  );

  const displayName = $derived(poi.customName ?? poi.name);
  const hasNotes = $derived(poi.notes !== null && poi.notes.trim() !== '');
  const src = $derived(photoUrl(poi.photoReference, 720));
</script>

<article class="card-poi">
  <button type="button" class="open-btn" aria-label="Open {displayName}" onclick={() => onOpen(poi)}>
    <div class="card-poi__photo">
      {#if src}
        <img src={src} alt="" loading="lazy" />
      {:else}
        <div class="aspect-square w-full" style="background: var(--color-surface-strong);"></div>
      {/if}

      {#if poi.rating !== null}
        <span class="photo-badge rating-badge">
          <Icon name="star" size={11} />
          <span class="tabular-nums">{poi.rating.toFixed(1)}</span>
        </span>
      {/if}
      {#if hasNotes}
        <span class="photo-badge notes-badge" title="Has notes" aria-label="Has notes">
          <Icon name="file-text" size={11} strokeWidth={2.25} />
        </span>
      {/if}
    </div>

    <div class="flex flex-col gap-1 min-w-0 text-col">
      <!-- The grid wrapper around truncating text is the standard
           "truncate inside flex" workaround. minmax(0, 1fr) forces
           the cell width to its parent regardless of min-content —
           something min-w-0 doesn't reliably do on every browser /
           flex configuration. Without this the h3's box grew to fit
           the nowrap text, truncate's overflow:hidden clipped only
           inside h3's already-overgrown box, and no ellipsis appeared. -->
      <div class="truncate-wrap">
        <h3 class="text-title m-0 truncate" style="color: var(--color-ink);">{displayName}</h3>
      </div>
      {#if poi.customName !== null && poi.customName !== poi.name}
        <div class="truncate-wrap">
          <p class="original-name truncate" title={poi.name}>orig. {poi.name}</p>
        </div>
      {/if}
      {#if poi.category}
        <p class="text-body-sm m-0" style="color: var(--color-muted);">
          {formatTypeLabel(poi.category)}
        </p>
      {/if}
      {#if poiGroups.length > 0}
        <div class="poi-groups">
          {#each poiGroups as g (g.id)}
            <span class="poi-group-chip">
              <span class="dot dot--{g.tint}"></span>
              <span class="poi-group-name">{g.name}</span>
            </span>
          {/each}
        </div>
      {/if}
    </div>
  </button>
</article>

<style>
  .open-btn {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    width: 100%;
    /* min-w-0 + max-w-100% keep the card from stretching past its
       grid cell. Note: NO overflow: hidden here — combined with the
       button's border-radius, that rounded the bottom-left corner
       *into* the category text below the photo, clipping it. The
       grid-truncate wrappers around the name + orig-name handle the
       bleed at the source. */
    min-width: 0;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    border-radius: var(--radius-md);
  }
  .open-btn:focus-visible {
    outline: 2px solid var(--color-ink);
    outline-offset: 2px;
  }
  .card-poi__photo { position: relative; border-radius: var(--radius-md); overflow: hidden; }
  .rating-badge {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .notes-badge {
    position: absolute;
    bottom: 0.75rem;
    right: 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
  }
  .truncate-wrap {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }
  .original-name {
    margin: 1px 0 0 0;
    font-size: 12px;
    color: var(--color-muted);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .poi-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-top: 0.25rem;
  }
  .poi-group-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: var(--color-surface-soft);
    color: var(--color-ink);
    border-radius: 9999px;
    padding: 0.125rem 0.5rem;
    height: 22px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    max-width: 100%;
  }
  .poi-group-chip .dot {
    width: 6px;
    height: 6px;
    flex: none;
  }
  .poi-group-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
